import { useFirestoreNoCompagny, useFirestoreNoCompagnyGeneralTwoOrderBy, useFirestoreNoCompagnyGeneralOrderBy, useFirestore, useFirestoreAllArrayContains } from '../../firebase/useFirestore';
import { client } from '../../hooks/Client';
import PageHeader from '../../components/layout/PageHeader';
import DraggableTableItems from '../../components/common/DraggableTableItems';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddItemTableRow from '../../components/common/AddItemTableRow';
import Tooltip from '../../components/common/Tooltip';
import { useState, useEffect, Fragment } from 'react';
import DeleteModal from "../../components/common/DeleteModal";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useHistory } from "react-router-dom";
import TOCProgress from '../../hooks/TOCProgress';
import Location from "../../hooks/Location"
import AddActivitySyncsModal from '../../components/portfolio/AddActivitySyncsModal';
import { db, timestamp } from '../../firebase/config';
import { v4 as uuid } from 'uuid';
import EffectMeta from '../../components/effects/EffectMeta';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddEffectSyncsModal from '../../components/portfolio/AddEffectSyncsModal';
import OutputMeta from '../../components/outputs/OutputMeta';
import AddTargetgroupsModal from '../../components/portfolio/AddTargetgroupsModal';
import AddCustomEventsModal from '../../components/portfolio/AddCustomEventsModal';
import FieldMeta from '../../components/Questionnaire/FieldMeta';
import TargetgroupMeta from '../../components/stakeholders/TargetgroupMeta';
import CustomEventFieldMeta from '../../components/events/CustomEventFieldMeta';
import EventFieldMeta from '../../components/events/EventFieldMeta';
import CopySynchronisationsModal from '../../components/portfolio/CopySynchronisationsModal';
import Modal from '../../components/common/Modal';
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard';
import normalizeStandard from '../../helpers/effects/normalizeStandard';
import clickableRowProps from '../../helpers/a11y/clickableRowProps';
import SyncGroupToggle from '../../components/portfolio/SyncGroupToggle';

const Synchronisations = () => {
  // State
  const [hoverIndex, setHoverIndex] = useState(null)
  const [placeholderIndex, setPlaceholderIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false)
  const [defDeleteModal, setDefDeleteModal] = useState(false)
  const [deleteDocid, setDeleteDocid] = useState('123')
  const [deleteName, setDeleteName] = useState('')
  const [showAddContainer, setShowAddContainer] = useState(false)
  const [addActivitySyncsModalOpen, setAddActivitySyncsModalOpen] = useState(false)
  const [addEffectSyncsModalOpen, setAddEffectSyncsModalOpen] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [addTargetgroupsModalOpen, setAddTargetgroupsModalOpen] = useState(false)
  const [addCustomFieldsModalOpen, setAddCustomFieldsModalOpen] = useState(false);
  const [selectedTargetgroups, setSelectedTargetgroups] = useState([])
  const [grouped, setGrouped] = useState([]);
  const [collapsedKeys, setCollapsedKeys] = useState(new Set());
  const [selectedCustomEvents, setSelectedCustomEvents] = useState([]);
  const [copyProjectModalOpen, setCopyProjectModalOpen] = useState(false);

  // Hooks
  const history = useHistory()
  const progress = TOCProgress()
  const projectId = Location()[3]

  // Firestore
  const syncs = useFirestoreNoCompagnyGeneralTwoOrderBy('Synchronisations', 'Parent', client, 'Project', projectId, 'Timestamp', 'desc');
  const projects = useFirestoreNoCompagny('CompagnyMeta', 'CompagnyID', projectId)
  const notifications = useFirestoreNoCompagnyGeneralOrderBy('Notifications', 'Reciever', client, 'Timestamp', 'desc');

  // helper: chunk Firestore "in" queries (limit 10)
  const chunk = (arr, size = 10) =>
    arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

  // helper: batch fetch to a map by "ID"
  const getDocsMapByID = async (collection, ids) => {
    const map = {};
    if (!ids || ids.length === 0) return map;

    for (const part of chunk([...new Set(ids)].filter(Boolean), 10)) {
      const snap = await db.collection(collection).where('ID', 'in', part).get();
      snap.forEach(doc => {
        const data = doc.data();
        map[data.ID] = data;
      });
    }
    return map;
  };

  // Set project name in state
  useEffect(() => {
    projects && projects.map(project => {
      setProjectName(project.CommunityName)
      return null;
    })
  }, [projects])

  // Restore collapsed state
  useEffect(() => {
    if (!projectId) return;
    try {
      const saved = localStorage.getItem(`sync-collapse:${projectId}`);
      if (saved) setCollapsedKeys(new Set(JSON.parse(saved)));
    } catch {}
  }, [projectId]);

  // Persist collapsed state
  useEffect(() => {
    if (!projectId) return;
    try {
      localStorage.setItem(`sync-collapse:${projectId}`, JSON.stringify([...collapsedKeys]));
    } catch {}
  }, [collapsedKeys, projectId]);

  const toggleGroup = (key) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Grouped questions, outputs, targetgroups by type
  useEffect(() => {
    const build = async () => {
      if (!syncs || syncs.length === 0) {
        setGrouped([]);
        return;
      }

      // separate by type (skip deleted in the UI)
      const effectSyncs      = syncs.filter(s => s.Type === 'MSIEffect'   && s.Status !== 'deleted');
      const outputSyncs      = syncs.filter(s => s.Type === 'Output'      && s.Status !== 'deleted');
      const questionSyncs    = syncs.filter(s => s.Type === 'Question'    && s.Status !== 'deleted');
      const targetgroupSyncs = syncs.filter(s => s.Type === 'Targetgroup' && s.Status !== 'deleted'); 
      const customEventSyncs = syncs.filter(s => s.Type === 'CustomEvent' && s.Status !== 'deleted');
      const eventSyncs       = syncs.filter(s => s.Type === 'Event'       && s.Status !== 'deleted');

      // ---------- OUTPUTS + EVENT FIELDS -> Activities ----------
      const outputIds       = [...new Set(outputSyncs.map(s => s.Output).filter(Boolean))];
      const outputsById     = await getDocsMapByID('Outputs', outputIds);

      // Event-field syncs resolve to an activity via EventFields → EventForms.
      const eventFieldIds   = [...new Set(eventSyncs.map(s => s.Event).filter(Boolean))];
      const eventFieldsById = await getDocsMapByID('EventFields', eventFieldIds);
      const eventFormIds    = [...new Set(eventFieldIds.map(id => eventFieldsById[id]?.EventId).filter(Boolean))];
      const eventFormsById  = await getDocsMapByID('EventForms', eventFormIds);

      const activityIds = [...new Set([
        ...outputIds.map(id => outputsById[id]?.ActivityID),
        ...eventFormIds.map(id => eventFormsById[id]?.ActivityID),
      ].filter(Boolean))];
      const activitiesById = await getDocsMapByID('Activities', activityIds);

      // Outputs and event fields share one group per activity.
      const activityGroupsByAct = {};
      const ensureActivityGroup = (actId) => {
        const key = `activity:${actId}`;
        if (!activityGroupsByAct[key]) {
          activityGroupsByAct[key] = {
            kind: 'group',
            scope: 'activity',
            key,
            label: activitiesById[actId]?.Activity || 'Onbekende activiteit',
            rows: [],
          };
        }
        return activityGroupsByAct[key];
      };

      for (const s of outputSyncs) {
        const actId = outputsById[s.Output]?.ActivityID || 'unknown';
        const group = ensureActivityGroup(actId);
        group.rows.push({ kind:'row', sync:s, groupKey:group.key });
      }

      for (const s of eventSyncs) {
        const formId = eventFieldsById[s.Event]?.EventId;
        const actId  = eventFormsById[formId]?.ActivityID || 'unknown';
        const group  = ensureActivityGroup(actId);
        group.rows.push({ kind:'row', sync:s, groupKey:group.key });
      }

      // ---------- QUESTIONS -> Effects (array) ----------
      const questionIds      = [...new Set(questionSyncs.map(s => s.Question).filter(Boolean))];
      const questionsById    = await getDocsMapByID('QuestionnaireFields', questionIds);
      const effectIdsFromQs  = [
        ...new Set(
          questionIds.flatMap(qid => (questionsById[qid]?.EffectId || []))
        )
      ].filter(Boolean);

      const effectsById = await getDocsMapByID('OutputEffects', effectIdsFromQs);

      const questionGroupsByEff = {};
      for (const s of questionSyncs) {
        const eids = questionsById[s.Question]?.EffectId || ['unknown'];
        for (const eid of eids) {
          const key   = `effect:${eid}`;
          const label = effectsById[eid]?.Effect || 'Onbekend effect';
          if (!questionGroupsByEff[key]) questionGroupsByEff[key] = { kind:'group', scope:'question', key, label, rows: [] };
          questionGroupsByEff[key].rows.push({ kind:'row', sync:s, groupKey:key });
        }
      }

      // ---------- EFFECTS (single bucket) ----------
      const effectGroup =
        effectSyncs.length
          ? [{
              kind:'group',
              scope:'effect',
              key:'effects',
              label:'Meetstandaard Effecten',
              rows: effectSyncs.map(s => ({ kind:'row', sync:s, groupKey:'effects' }))
            }]
          : [];

      // ---------- TARGETGROUPS (single bucket) ---------- 
      const targetgroupGroup =
        targetgroupSyncs.length
          ? [{
              kind: 'group',
              scope: 'targetgroup',
              key: 'targetgroups',
              label: 'Doelgroepen',
              rows: targetgroupSyncs.map(s => ({ kind:'row', sync:s, groupKey:'targetgroups' }))
            }]
          : [];

      // ---------- CUSTOM EVENTS (single bucket) ----------
      const customEventGroup =
        customEventSyncs.length
          ? [{
              kind: 'group',
              scope: 'customevent',
              key: 'customevents',
              label: 'Custom events',
              rows: customEventSyncs.map(s => ({ kind: 'row', sync: s, groupKey: 'customevents' }))
            }]
          : [];


      // sort group headings alphabetically for stable UI
      const activityGroups = Object.values(activityGroupsByAct).sort((a,b) => a.label.localeCompare(b.label));
      const questionGroups = Object.values(questionGroupsByEff).sort((a,b) => a.label.localeCompare(b.label));

      // final list (order: Effects, Activities, Questions by Effect, Targetgroups)
      setGrouped([
        ...effectGroup,
        ...activityGroups,
        ...questionGroups,
        ...targetgroupGroup,
        ...customEventGroup,
      ]);
    };

    build();
  }, [syncs]);

  // Delete modal
  const deleteModalHandler = (e) => {
    const docid = e.currentTarget.dataset.docid
    const deleteName = e.currentTarget.dataset.deletename

    setDeleteDocid(docid)
    setDeleteName(deleteName)
    setDeleteModal(!deleteModal)
  }

  // Definitive delete modal handler
  const defDeleteModalHandler = (e) => {
    const docid = e.currentTarget.dataset.docid
    const deleteName = e.currentTarget.dataset.deletename

    setDeleteDocid(docid)
    setDeleteName(deleteName)
    setDefDeleteModal(!defDeleteModal);
  };

  // Write one requested sync row + its notification.
  const writeSyncRequest = async ({ type, idField, idValue, syncPosition, notifPosition, extra }) => {
    const syncId = uuid()

    await db.collection('Synchronisations').add({
      Parent: client,
      Project: projectId,
      // Denormalised party tenants for the Synchronisations rule/query gate.
      // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
      Members: [client, projectId].filter(Boolean),
      [idField]: idValue,
      ID: syncId,
      Timestamp: timestamp,
      Type: type,
      Status: 'requested',
      Initiator: 'portfolio',
      Position: syncPosition,
      ...(extra || {}),
    });

    await db.collection('Notifications').add({
      Type: 'sync-request',
      Reciever: projectId,
      Sender: client,
      Timestamp: timestamp,
      Read: false,
      ID: uuid(),
      SyncType: type,
      [idField]: idValue,
      Position: notifPosition,
      SyncId: syncId,
    });
  };

  // Add activity syncs — outputs + non-output event fields picked per activity.
  const addActivitySyncs = async ({ outputs: pickedOutputs, eventFields: pickedEventFields }) => {
    const baseSyncPos = syncs?.length || 0;
    const baseNotifPos = notifications?.length || 0;
    let i = 0;

    for (const output of pickedOutputs || []) {
      await writeSyncRequest({
        type: 'Output',
        idField: 'Output',
        idValue: output.ID,
        syncPosition: baseSyncPos + i + 1,
        notifPosition: baseNotifPos + i + 1,
      });
      i++;
    }

    for (const field of pickedEventFields || []) {
      await writeSyncRequest({
        type: 'Event',
        idField: 'Event',
        idValue: field.ID,
        syncPosition: baseSyncPos + i + 1,
        notifPosition: baseNotifPos + i + 1,
      });
      i++;
    }
  };

  // Add effect syncs — whole MSI effects + individually picked questions.
  const addEffectSyncs = async ({ effects: pickedEffects, questions: pickedQuestions }) => {
    const baseSyncPos = syncs?.length || 0;
    const baseNotifPos = notifications?.length || 0;
    let i = 0;

    for (const effect of pickedEffects || []) {
      // Find one portfolio matrix indicator tied to the effect — its field ID
      // is the linkField fetchHierarchyData traverses for standardised effects.
      const indicatorsSnap = await db.collection('QuestionnaireFields')
        .where('EffectId', 'array-contains', effect.ID)
        .get();

      const portfolioIndicators = indicatorsSnap.docs
        .map(d => d.data())
        .filter(ind => ind.Type === 'matrix-one' || ind.Type === 'matrix-multiple')
        .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));

      const portfolioQuestionId = portfolioIndicators[0]?.ID || null;

      await writeSyncRequest({
        type: 'MSIEffect',
        idField: 'Effect',
        idValue: effect.ID,
        syncPosition: baseSyncPos + i + 1,
        notifPosition: baseNotifPos + i + 1,
        extra: {
          // Canonical string shape — never boolean.
          // AI-CONTEXT: See docs/pitfalls.md#database-string-enum-boolean-coercion
          Standard: normalizeStandard(effect.Standard),
          MSIId: effect.MSIId || '',
          ...(portfolioQuestionId ? { Question: portfolioQuestionId } : {}),
        },
      });
      i++;
    }

    for (const question of pickedQuestions || []) {
      await writeSyncRequest({
        type: 'Question',
        idField: 'Question',
        idValue: question.ID,
        syncPosition: baseSyncPos + i + 1,
        notifPosition: baseNotifPos + i + 1,
      });
      i++;
    }
  };

  // Delete sync
  const deleteSync =  async () => {
    await db.collection('Synchronisations').doc(deleteDocid).update({
      Status: 'deleted'
    })
  }

  // Definitive delete modal handler
  const definitiveDeleteSync = async () => {
    await db.collection('Synchronisations').doc(deleteDocid).delete();
    setDefDeleteModal(false);
  };

  // Sync status
  const status = (statusCode) => {
    switch (statusCode) {
      case "requested":
        return { text: "Aangevraagd", color: "#FFA500" };
      case "accepted":
        return { text: "Geaccepteerd", color: "#008000" };
      case "paired":
        return { text: "Gekoppeld", color: "#008000" };
      case "declined":
        return { text: "Geweigerd", color: "#FF0000" };
      case "deleted":
        return { text: "Verwijderd door portfolio", color: "#000000" };
      case "deleted-project":
        return { text: "Verwijderd door project", color: "#000000" };
      default:
        return { text: "Onbekende status", color: "#000000" };
    }
  };

  // Sync type
  const syncType = (type) => {
    switch (type) {
      case 'MSIEffect':
        return 'Meetstandaard effect';
      case 'Effect':
        return 'Effect';
      case 'Output':
        return 'Output';
      case 'Question':
        return 'Vraag';
      case 'Targetgroup':
        return 'Doelgroep';
      case 'CustomEvent':
        return 'Custom veld';
      case 'Event':
        return 'Activiteitveld';
      default:
        return 'Onbekend';
    }
  };


  // Add target groups
  const addTargetgroups = async () => {
    for (const group of selectedTargetgroups) {
      const syncId = uuid()

      await db.collection('Synchronisations').add({
        Parent: client,
        Project: projectId,
        // Denormalised party tenants for the Synchronisations rule/query gate.
        // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
        Members: [client, projectId].filter(Boolean),
        Targetgroup: group.ID,
        ID: syncId,
        Timestamp: timestamp,
        Type: 'Targetgroup',
        Status: 'requested',
        Initiator: 'portfolio',
        Position: (syncs?.length || 0) + 1,
      });

      await db.collection('Notifications').add({
        Type: 'sync-request',
        Reciever: projectId,
        Sender: client,
        Timestamp: timestamp,
        Read: false,
        ID: uuid(),
        SyncType: 'Targetgroup',
        Targetgroup: group.ID,
        Position: (notifications?.length || 0) + 1,
        SyncId: syncId
      });
    }

    setSelectedTargetgroups([])
    setAddTargetgroupsModalOpen(false)
  }

  // Add custom events
  const addCustomEvents = async () => {

    for (const event of selectedCustomEvents) {
      const syncId = uuid()

      await db.collection('Synchronisations').add({
        Parent: client,
        Project: projectId,
        // Denormalised party tenants for the Synchronisations rule/query gate.
        // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
        Members: [client, projectId].filter(Boolean),
        CustomEvent: event.ID,
        ID: syncId,
        Timestamp: timestamp,
        Type: 'CustomEvent',
        Status: 'requested',
        Initiator: 'portfolio',
        Position: (syncs?.length || 0) + 1,
      });

      await db.collection('Notifications').add({
        Type: 'sync-request',
        Reciever: projectId,
        Sender: client,
        Timestamp: timestamp,
        Read: false,
        ID: uuid(),
        SyncType: 'CustomEvent',
        CustomEvent: event.ID,
        Position: (notifications?.length || 0) + 1,
        SyncId: syncId
      });
    }

    setSelectedCustomEvents([])
    setAddCustomFieldsModalOpen(false)

  }

  // Copy synchronisations from another project (receives pre-selected syncs from modal)
  const copySynchronisations = async (selectedSyncs) => {
    if (!selectedSyncs || selectedSyncs.length === 0) return;

    for (let i = 0; i < selectedSyncs.length; i++) {
      const s = selectedSyncs[i];
      await db.collection('Synchronisations').add({
        Parent: client,
        Project: projectId,
        // Denormalised party tenants for the Synchronisations rule/query gate.
        // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
        Members: [client, projectId].filter(Boolean),
        ID: uuid(),
        Timestamp: timestamp,
        Type: s.Type,
        Status: 'requested',
        Initiator: 'portfolio',
        Position: (syncs?.length || 0) + i + 1,
        ...(s.Output      ? { Output: s.Output }           : {}),
        ...(s.Effect      ? { Effect: s.Effect }           : {}),
        ...(s.Question    ? { Question: s.Question }       : {}),
        ...(s.Targetgroup ? { Targetgroup: s.Targetgroup } : {}),
        ...(s.CustomEvent ? { CustomEvent: s.CustomEvent } : {}),
        ...(s.Type === 'MSIEffect' ? {
          // Copied sync docs can carry the legacy boolean shape — normalize
          // to the canonical string shape on write.
          // AI-CONTEXT: See docs/pitfalls.md#database-string-enum-boolean-coercion
          Standard: normalizeStandard(s.Standard),
          MSIId: s.MSIId || '',
        } : {}),
      });
    }

    await db.collection('Notifications').add({
      Type: 'sync-copy',
      Reciever: projectId,
      Sender: client,
      Timestamp: timestamp,
      Read: false,
      ID: uuid(),
      Position: (notifications?.length || 0) + 1,
    });
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader 
          heading={`Koppelingen`}
          subHeading={projectName}
          description={`Bekijk en beheer hier de koppelingen met ${projectName}.`}
          AI={false}
        />

        <ReadOnlyGuard edit="portfolio.edit">
        {(canEdit) => (
        <>
        <div className="table-container dashboard-container">
          <table>
            <tbody>
              <tr>
                <th>KOPPELING</th>
                <th>TYPE</th>
                <th>STATUS</th>
                <th>DETAILS</th>
                <th>VERWIJDEREN</th>
              </tr>

              {grouped.map(group => {
                const isCollapsed = collapsedKeys.has(group.key);
                const headerLabel =
                  group.scope === 'activity'    ? `Activiteit: ${group.label}` :
                  group.scope === 'question'    ? `Effect: ${group.label}`     :
                  group.scope === 'targetgroup' ? `Doelgroepen`                : 
                  group.scope === 'effect'      ? `Meetstandaard Effecten`     :
                  group.scope === 'customevent' ? `Custom velden`              :
                  group.label;

                return (
                  <Fragment key={group.key}>
                    {/* Group header row */}
                    <tr className="group-row">
                      <th colSpan="6" style={{ backgroundColor: '#f2f2f2', padding: 0 }}>
                        <SyncGroupToggle
                          groupKey={group.key}
                          label={headerLabel}
                          count={group.rows.length}
                          collapsed={isCollapsed}
                          onToggle={toggleGroup}
                        />
                      </th>
                    </tr>

                    {/* Group body (hidden when collapsed) */}
                    {!isCollapsed && group.rows.map(({ sync }, index) => (
                      <DraggableTableItems
                        key={sync.ID}
                        item={sync}
                        input={
                            <p>
                              {['Effect','MSIEffect'].includes(sync.Type) ? (
                                <EffectMeta item={sync.Effect} />
                              ) : sync.Type === 'Output' ? (
                                <OutputMeta id={sync.Output} />
                              ) : sync.Type === 'Question' ? (
                                <FieldMeta item={sync.Question} />
                              ) : sync.Type === 'Targetgroup' ? (     
                                <TargetgroupMeta targetgroupID={sync.Targetgroup} />
                              ) : sync.Type === 'CustomEvent' ? (
                                <CustomEventFieldMeta fieldId={sync.CustomEvent} />
                              ) : sync.Type === 'Event' ? (
                                <EventFieldMeta fieldId={sync.Event} />
                              ) : null}
                            </p>
                          }
                        parentIndex={index}
                        collection={'Synchronisations'}
                        itemArray={group.rows.map(r => r.sync)}  // keep drag confined within the group
                        setHoverIndex={setHoverIndex}
                        hoverIndex={hoverIndex}
                        title={sync.CommunityName}
                        setPlaceholderIndex={setPlaceholderIndex}
                      >
                        <td>
                          <p>{syncType(sync.Type)}</p>
                        </td>

                        <td>
                          {sync.Initiator === 'project' ? (
                            <p className="sync-status-by-project">Gekoppeld door project</p>
                          ) : (
                            <p style={{ color: status(sync.Status).color }}>
                              {status(sync.Status).text}
                            </p>
                          )}
                        </td>

                        <td>
                          <SearchOutlinedIcon onClick={() => history.push(`/${client}/portfoliosyncdetails/${sync.ID}`)} />
                        </td>

                        <td>
                          {canEdit && sync.Initiator !== 'project' && (
                            <Tooltip content='Koppeling verwijderen' top='-60px'>
                              <DeleteOutlinedIcon
                                className='table-delete-icon'
                                data-id={sync.ID}
                                data-docid={sync.docid}
                                data-deletename={sync.Type}
                                onClick={sync.Status === 'deleted' ? defDeleteModalHandler : deleteModalHandler}
                              />
                            </Tooltip>
                          )}
                        </td>
                      </DraggableTableItems>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {canEdit && (
            <AddItemTableRow toolTipContent={'Project toevoegen'} onClick={() => setShowAddContainer(true)} />
          )}
          <Modal
            modalOpen={showAddContainer}
            setModalOpen={setShowAddContainer}
            pageHeader={'Toevoegen'}
            hideConfirm={true}
            content={
              <div className="options-modal-list">
                <div className="options-modal-item" {...clickableRowProps(() => { setShowAddContainer(false); setAddActivitySyncsModalOpen(true); })}>
                  <div className="options-modal-item__icon"><AssignmentOutlinedIcon /></div>
                  <div className="options-modal-item__body">
                    <p className="options-modal-item__title">Activiteiten</p>
                    <p className="options-modal-item__description">Koppel de outputs en velden van een activiteit.</p>
                  </div>
                  <ChevronRightIcon className="options-modal-item__chevron" />
                </div>
                <div className="options-modal-item" {...clickableRowProps(() => { setShowAddContainer(false); setAddEffectSyncsModalOpen(true); })}>
                  <div className="options-modal-item__icon"><InsightsOutlinedIcon /></div>
                  <div className="options-modal-item__body">
                    <p className="options-modal-item__title">Effecten</p>
                    <p className="options-modal-item__description">Koppel een meetstandaard-effect of losse vragen van een effect.</p>
                  </div>
                  <ChevronRightIcon className="options-modal-item__chevron" />
                </div>
                <div className="options-modal-item" {...clickableRowProps(() => { setShowAddContainer(false); setAddTargetgroupsModalOpen(true); })}>
                  <div className="options-modal-item__icon"><GroupsOutlinedIcon /></div>
                  <div className="options-modal-item__body">
                    <p className="options-modal-item__title">Doelgroepen</p>
                    <p className="options-modal-item__description">Koppel doelgroepen met dit project.</p>
                  </div>
                  <ChevronRightIcon className="options-modal-item__chevron" />
                </div>
                <div className="options-modal-item" {...clickableRowProps(() => { setShowAddContainer(false); setAddCustomFieldsModalOpen(true); })}>
                  <div className="options-modal-item__icon"><TuneOutlinedIcon /></div>
                  <div className="options-modal-item__body">
                    <p className="options-modal-item__title">Custom velden</p>
                    <p className="options-modal-item__description">Koppel herbruikbare custom velddefinities.</p>
                  </div>
                  <ChevronRightIcon className="options-modal-item__chevron" />
                </div>
                <div className="options-modal-item" {...clickableRowProps(() => { setShowAddContainer(false); setCopyProjectModalOpen(true); })}>
                  <div className="options-modal-item__icon"><ContentCopyOutlinedIcon /></div>
                  <div className="options-modal-item__body">
                    <p className="options-modal-item__title">Kopiëren van ander project</p>
                    <p className="options-modal-item__description">Neem bestaande koppelingen van een ander project over.</p>
                  </div>
                  <ChevronRightIcon className="options-modal-item__chevron" />
                </div>
              </div>
            }
          />
        </div>
        </>
        )}
        </ReadOnlyGuard>
      </div>

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteSync}
        deleteName={deleteName}
      />
      <DeleteModal
        deleteModal={defDeleteModal}
        setDeleteModal={setDefDeleteModal}
        deleteItem={definitiveDeleteSync}
        deleteName={deleteName}
        itemId={deleteDocid}
      />
      <AddActivitySyncsModal
        modalOpen={addActivitySyncsModalOpen}
        setModalOpen={setAddActivitySyncsModalOpen}
        onConfirm={addActivitySyncs}
      />
      <AddEffectSyncsModal
        modalOpen={addEffectSyncsModalOpen}
        setModalOpen={setAddEffectSyncsModalOpen}
        onConfirm={addEffectSyncs}
      />
      <AddTargetgroupsModal
        setAddTargetgroupsModalOpen={setAddTargetgroupsModalOpen} 
        addTargetgroupsModalOpen={addTargetgroupsModalOpen} 
        addTargetgroups={addTargetgroups} 
        selectedTargetgroups={selectedTargetgroups} 
        setSelectedTargetgroups={setSelectedTargetgroups}
      />
      <AddCustomEventsModal
        setAddCustomEventsModalOpen={setAddCustomFieldsModalOpen}
        addCustomEventsModalOpen={addCustomFieldsModalOpen}
        addCustomEvents={addCustomEvents}
        selectedCustomEvents={selectedCustomEvents}
        setSelectedCustomEvents={setSelectedCustomEvents}
      />
      <CopySynchronisationsModal
        modalOpen={copyProjectModalOpen}
        setModalOpen={setCopyProjectModalOpen}
        onCopy={copySynchronisations}
      />
    </div>
  )
}

export default Synchronisations
